import { clsx, type ClassValue } from "clsx"
import type { DeepPartial } from "@/types"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export * from "./date_utils"

export function nonNullabel<T>(value: T): value is NonNullable<T> {
  return value != null
}

export function slugify(text: string) {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w-]+/g, "") // Remove all non-word chars
    .replace(/--+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, "") // Trim - from end of text
}

// TODO: very crude implementation of deep merge
export function merge<T extends Record<string | number, any>>(
  target: T,
  source: DeepPartial<T>,
) {
  for (const key in source) {
    const targetValue = target[key]
    const sourceValue = source[key]
    if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
      const newValue = Array.from(new Set([...targetValue, ...sourceValue]))
      Object.assign(target, { [key]: newValue })
    } else if (isObject(targetValue) && isObject(sourceValue)) {
      merge(targetValue, sourceValue)
    } else {
      Object.assign(target, { [key]: sourceValue })
    }
  }
  return target
}

function isObject(obj: unknown) {
  if (typeof obj === "object" && obj !== null) {
    if (typeof Object.getPrototypeOf === "function") {
      const prototype = Object.getPrototypeOf(obj)
      return prototype === Object.prototype || prototype === null
    }
    return Object.prototype.toString.call(obj) === "[object Object]"
  }
  return false
}

/**
 * withCurrency
 * @param {number} amount in cents
 * @param {object} options
 * @param {string} options.currency
 * @param {string} options.locale
 * @returns {string}
 */
export const withCurrency = (
  amount = 0,
  { currency = "USD", locale = "en-US" } = {},
) => {
  const formatter = new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
  const formattedAmount = formatter.format(amount / 100)
  if (formattedAmount.endsWith(".00")) {
    return formatter.format(amount / 100).split(".")[0]
  }
  return formattedAmount
}
